import { useQuery } from '@tanstack/react-query';
import { ApiClient } from '@utils/apiClient';
import type { Organization, OrganizationFeature } from '../types/shared';

// One cached request for /organizations/current/; consumers pick their fields via `select`.
export const currentOrganizationQueryKey = ['organization', 'current'] as const;

export const useCurrentOrganization = <T>(select: (organization: Organization | null) => T) =>
  useQuery({
    queryKey: currentOrganizationQueryKey,
    queryFn: async (): Promise<Organization | null> => {
      const response = await ApiClient.get('/organizations/current/');
      if (!response.ok) return null;
      return (await response.json()) as Organization;
    },
    staleTime: 5 * 60 * 1000,
    select,
  });

const selectFeatures = (organization: Organization | null): OrganizationFeature[] =>
  organization?.features ?? [];

// Tier-gated features of the current organization (empty while loading or on error).
export const useOrganizationFeatures = () => {
  const { data, isLoading } = useCurrentOrganization(selectFeatures);
  const features = data ?? [];
  return {
    features,
    isLoading,
    hasFeature: (feature: OrganizationFeature) => features.includes(feature),
  };
};
